import React, { useState, useEffect } from 'react';
import {
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined,
  DollarOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  WalletOutlined,
  FileOutlined,
  FilePdfOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Tooltip,
  message,
  Modal,
  Popconfirm,
  Row,
  Col,
  DatePicker,
  Statistic,
  Descriptions,
  Pagination
} from 'antd';
import * as XLSX from 'xlsx';
import { getReceipts, getReceiptById, deleteReceipt } from '../Services/Services';
import html2pdf from 'html2pdf.js';
import logo from '../Logo.png'

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ReceiptList = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobilePage, setMobilePage] = useState(1);
  const mobilePageSize = 5;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (search.trim()) filters.search = search.trim();
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;

      const response = await getReceipts(filters);

      if (response.success) {
        setReceipts(response.results || []);
        if (response.results?.length === 0) {
          message.info("No receipts found");
        } else {
          message.success(`Found ${response.count || 0} receipts`);
        }
      } else {
        setReceipts([]);
        message.error(response.message || 'Failed to fetch receipts');
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      message.error('Failed to fetch receipts');
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  const handleDateRangeChange = (dates, dateStrings) => {
    setFromDate(dateStrings[0]);
    setToDate(dateStrings[1]);
  };

  const handleSearch = () => {
    fetchReceipts();
  };

  const handleReset = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setTimeout(() => fetchReceipts(), 100);
  };

  const viewReceipt = async (id) => {
    try {
      const response = await getReceiptById(id);
      if (response.success) {
        setSelectedReceipt(response.data);
        setShowModal(true);
      } else {
        message.error('Failed to load receipt details');
      }
    } catch (error) {
      message.error('Failed to load receipt details');
    }
  };

  const handleDeleteReceipt = async (id, receiptNo) => {
    setLoading(true);
    try {
      await deleteReceipt(id);
      message.success(`Receipt ${receiptNo} deleted successfully`);
      await fetchReceipts();
    } catch (error) {
      message.error('Failed to delete receipt');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (receipt) => {
    setPdfLoading(true);

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const formatCurrency = (amount) => {
      if (isNaN(amount) || amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    const titleText = 'PAYMENT RECEIPT';
    const refLabel = 'Receipt No';

    const description = receipt.narration || receipt.rec_category || 'Payment Received';
    const receiptDate = receipt.receipt_date || (receipt.date ? new Date(receipt.date).toLocaleDateString('en-IN') : formattedDate);
    const totalAmount = parseFloat(receipt.amount) || 0;

    const itemsRows = `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; text-align: center; width: 10%;">1</td>
        <td style="padding: 8px; width: 50%;">
          <div style="font-weight: 600;">${description}</div>
          <div style="font-size: 9px; color: #64748b; margin-top: 2px;">Category: ${receipt.rec_category || '-'} | Mode: ${receipt.rec_mode || '-'}</div>
        </td>
        <td style="padding: 8px; text-align: center; width: 20%;">${receiptDate}</td>
        <td style="padding: 8px; text-align: right; font-weight: 600; width: 20%;">₹${formatCurrency(totalAmount)}</td>
      </tr>
    `;

    let paymentSectionHtml = '';
    if (receipt.bill_no || receipt.ref_no) {
      paymentSectionHtml = `
        <div style="background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%); border-left: 4px solid #2e7d32; padding: 10px 15px; margin: 15px 0; border-radius: 6px; font-size: 11px;">
          <div style="display: flex; gap: 20px; flex-wrap: wrap;">
            ${receipt.bill_no ? `<span><strong>📄 Against Bill No:</strong> ${receipt.bill_no}</span>` : ''}
            ${receipt.ref_no ? `<span><strong>🔗 Ref/Txn No:</strong> ${receipt.ref_no}</span>` : ''}
            <span><strong>📊 Status:</strong> <span style="color: #2e7d32; font-weight: bold;">Fully Settled</span></span>
          </div>
        </div>
      `;
    }

    const element = document.createElement('div');
    element.innerHTML = `
    <div style="width: 100%; max-width: 1000px; margin: 0 auto; background: #ffffff; font-family: 'Times New Roman', Times, serif; box-sizing: border-box; border: 1px solid #a3a3a3; padding: 15px; border-radius: 4px;">
      
      <div style="height: 5px; background: linear-gradient(90deg, #103f7a 75%, #1c7c34 75%); margin: -15px -15px 15px -15px; border-radius: 4px 4px 0 0;"></div>

      <div style="padding: 5px 10px 15px 10px; background: #ffffff;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 20px;">
          
          <div style="width: 85px; min-width: 85px; display: flex; align-items: center; justify-content: center;">
            <img src="${logo}" style="width: 100%; height: auto; object-fit: contain;" />
          </div>
          
          <div style="flex:1; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #103f7a; letter-spacing: 0.5px; line-height: 1.2;">D.K. TRANSPORT CONSULTANCY</div>
            <div style="font-size: 10.5px; color: #334155; margin-top: 5px; text-transform: uppercase; letter-spacing: 0.3px;">
              PARKING NO. 1 BABLA FUELS KE BAJU T P NAGAR RAWABHATA RAIPUR
            </div>
            <div style="font-size: 10px; color: #1c7c34; font-style: italic; margin-top: 3px;">
              (Deal in all RTO related matters, Insurance - 2 Wheeler, 4 Wheeler, Commercial Vehicles)
            </div>
          </div>
          
          <div style="text-align: right; border-left: 1px solid #cbd5e1; padding-left: 15px; min-width: 150px; font-size: 11px; color: #334155;">
            <div style="font-size: 12px; font-weight: bold; color: #103f7a; margin-bottom: 3px;">Durgesh Kumar Siware</div>
            <div style="margin-bottom: 2px;">📞 79749 97280</div>
            <div>📞 73890 42205</div>
          </div>
        </div>
      </div>

      <div style="text-align: center; background: #103f7a; color: #ffffff; font-size: 13px; font-weight: bold; padding: 4px 0; margin: 5px 10px 15px 10px; letter-spacing: 3px; border-radius: 2px;">
        RECEIPT / ACKNOWLEDGEMENT
      </div>

      <div style="margin: 0 10px 15px 10px; border: 1px solid #cbd5e1; border-radius: 3px; overflow: hidden; background: #fdfdfd;">
        <table style="width: 100%; border-collapse: collapse; font-size: 11px; font-family: 'Times New Roman', Times, serif;">
          <tr>
            <td style="padding: 10px 12px; width: 25%; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0;">
              <span style="color: #525252; font-weight: bold;">${refLabel}:</span> 
              <span style="color: #000000; margin-left: 4px;">${receipt.receipt_no || '-'}</span>
            </td>
            <td style="padding: 10px 12px; width: 25%; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0;">
              <span style="color: #525252; font-weight: bold;">Date:</span> 
              <span style="color: #000000; margin-left: 4px;">${receiptDate}</span>
            </td>
            <td style="padding: 10px 12px; width: 30%; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0;">
              <span style="color: #525252; font-weight: bold;">Received From:</span> 
              <span style="color: #000000; font-weight: bold; margin-left: 4px;">${receipt.received_from || '-'}</span>
            </td>
            <td style="padding: 10px 12px; width: 20%; border-bottom: 1px solid #e2e8f0;">
              <span style="color: #525252; font-weight: bold;">Mobile:</span> 
              <span style="color: #000000; margin-left: 4px;">${receipt.mobile_no || 'N/A'}</span>
            </td>
          </tr>
          <tr>
            <td colspan="4" style="padding: 8px 12px; font-size: 10.5px; text-align: right; color: #64748b; background: #ffffff;">
              Print Date: <span style="font-weight: bold; color: #334155;">${formattedDate}</span>
            </td>
          </tr>
        </table>
      </div>

      <div style="padding: 5px 10px 15px 10px; position: relative;">
        <div style="position: absolute; top: 45%; left: 50%; transform: translate(-50%, -50%) rotate(-12deg); width: 30%; opacity: 0.03; pointer-events: none;">
          <img src="${logo}" style="width: 100%; height: auto;" />
        </div>

        <div style="font-size: 12px; font-weight: bold; color: #103f7a; margin-bottom: 10px; padding-bottom: 3px; border-bottom: 1.5px solid #103f7a; text-transform: uppercase; letter-spacing: 0.5px;">
          ${titleText}
        </div>

        <table style="width: 100%; border-collapse: collapse; font-size: 11.5px; font-family: 'Times New Roman', Times, serif; border: 1px solid #a3a3a3; margin-bottom: 15px;">
          <thead>
            <tr style="background: #103f7a; color: #ffffff; font-weight: bold;">
              <th style="width: 8%; text-align: center; padding: 8px; border-right: 1px solid #cbd5e1; font-size: 11px;">#</th>
              <th style="width: 54%; text-align: left; padding: 8px; border-right: 1px solid #cbd5e1; font-size: 11px;">DESCRIPTION / PURPOSE</th>
              <th style="width: 18%; text-align: center; padding: 8px; border-right: 1px solid #cbd5e1; font-size: 11px;">RECEIPT DATE</th>
              <th style="width: 20%; text-align: right; padding: 8px; font-size: 11px;">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            ${itemsRows}
          </tbody>
          <tfoot>
            <tr style="background: #fbfbfb; border-top: 1.5px solid #a3a3a3; font-weight: bold;">
              <td colspan="2" style="padding: 10px; text-align: right; font-size: 11.5px; color: #103f7a; border-right: 1px solid #cbd5e1;">TOTAL RECEIVED AMOUNT</td>
              <td style="padding: 10px; text-align: center; background: #f4f4f5; border-right: 1px solid #cbd5e1; color: #334155; font-size: 10.5px;">INR</td>
              <td style="padding: 10px; text-align: right; font-size: 13.5px; color: #103f7a; background: #f0f7ff;">₹${formatCurrency(totalAmount)}</td>
            </tr>
          </tfoot>
        </table>

        <div style="margin-bottom: 15px;">
          ${paymentSectionHtml}
        </div>

        <div style="margin: 5px 0 20px 0; padding: 10px 12px; background: #f7faff; border-left: 4px solid #103f7a; border-radius: 3px; font-size: 11px; display: flex; flex-wrap: wrap; justify-content: space-between; gap: 15px; border-top: 1px solid #e0f2fe; border-right: 1px solid #e0f2fe; border-bottom: 1px solid #e0f2fe;">
          <div><strong style="color: #103f7a;">🏦 A/C NO:</strong> <span style="font-weight: bold;">38473468220</span></div>
          <div><strong style="color: #103f7a;">🏛️ IFSC:</strong> <span style="font-weight: bold;">SBIN0002852</span></div>
          <div><strong style="color: #103f7a;">👤 NAME:</strong> <span style="text-transform: uppercase;">Durgesh Kumar Siware</span></div>
          <div><strong style="color: #103f7a;">📞 MO:</strong> <span>7974997280, 7389042205</span></div>
        </div>

        <div style="display: flex; justify-content: space-between; margin: 35px 15px 10px 15px; font-size: 11px;">
          <div style="text-align: center; width: 30%;">
            <div style="border-bottom: 1px solid #cbd5e1; height: 35px; margin-bottom: 5px;"></div>
            <div style="color: #4b5563;">Customer Signature</div>
          </div>
          <div style="text-align: center; width: 30%;">
            <div style="font-weight: bold; color: #103f7a; margin-bottom: 20px; font-size: 11.5px;">Durgesh Siware</div>
            <div style="border-top: 1px solid #cbd5e1; padding-top: 5px; color: #4b5563;">Authorized Signature</div>
          </div>
        </div>
      </div>

      <div style="background:#0d1e3d; width:100%; border-radius: 2px; overflow: hidden; margin-top: 5px; font-family: Arial, Helvetica, sans-serif;">
        <div style="background:#103f7a; padding:8px 15px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px;">
          <div style="font-size:8.5px; font-weight:bold; color:#ffffff; letter-spacing: 0.3px;">📍 Near Babla Fuels, Piyush Seat Cover Road, Parking No. 1, Transport Nagar, Raipur (C.G.) 493221</div>
          <div style="background:#1c7c34; padding:3px 12px; border-radius:12px; font-size:8.5px; font-weight:bold; color:#ffffff;">✉ durgeshshivare@gmail.com</div>
        </div>
        <div style="background:#ffffff; padding:5px 15px; display:flex; justify-content:flex-end; gap:15px; align-items: center;">
          <svg style="height:16px;" viewBox="0 0 100 40" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="40" rx="3" fill="#0f4c81"/><circle cx="20" cy="20" r="10" fill="white" opacity="0.2"/><text x="60" y="21" font-family="sans-serif" font-weight="bold" font-size="7" fill="white" text-anchor="middle">UNITED INDIA</text><text x="60" y="30" font-family="sans-serif" font-size="5.5" fill="#cbd5e1" text-anchor="middle">INSURANCE</text></svg>
          <svg style="height:16px;" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="17" fill="#103f7a" stroke="#7ac143" stroke-width="1.5"/><path d="M15,12 L18,10 L22,13 L25,11 L28,16 L24,22 L26,28 L20,32 L16,28 L13,24 L16,18 Z" fill="white" opacity="0.85"/></svg>
        </div>
        <div style="background:#051429; padding:6px 15px; text-align:center;">
          <div style="display:flex; flex-wrap:wrap; justify-content:center; gap:3px 12px; font-weight: normal;">
            <span style="font-size:8px; color:#cbd5e1;">ड्राइविंग लाइसेंस</span>
            <span style="font-size:8px; color:#cbd5e1;">इंश्योरेंस</span>
            <span style="font-size:8px; color:#cbd5e1;">रोड टैक्स</span>
            <span style="font-size:8px; color:#cbd5e1;">परमिट</span>
            <span style="font-size:8px; color:#cbd5e1;">नाम ट्रांसफर</span>
            <span style="font-size:8px; color:#cbd5e1;">HSRP नं. प्लेट</span>
            <span style="font-size:8px; color:#cbd5e1;">फिटनेस</span>
          </div>
        </div>
        <div style="font-size:6px; color:#4b5563; text-align:center; padding:3px; background:#020b17; font-weight: bold; letter-spacing: 0.5px;">powered by yashvitech IT solution</div>
      </div>
    </div>
  `;

    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `Receipt_${receipt.receipt_no || 'receipt'}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true
      },
      jsPDF: {
        unit: 'in',
        format: 'a4',
        orientation: 'portrait'
      }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      setPdfLoading(false);
      message.success('PDF downloaded successfully!');
    }).catch((error) => {
      console.error('PDF generation error:', error);
      setPdfLoading(false);
      message.error('Failed to generate PDF');
    });
  };

  const exportToExcel = () => {
    setExportLoading(true);
    try {
      if (receipts.length === 0) {
        message.warning("No data to export!");
        return;
      }

      const excelData = receipts.map(receipt => ({
        "Receipt No": receipt.receipt_no || '-',
        "Date": receipt.receipt_date || '-',
        "Received From": receipt.received_from || '-',
        "Against Bill No": receipt.bill_no || '-',
        "Payment Mode": receipt.rec_mode || '-',
        "Category": receipt.rec_category || '-',
        "Reference No": receipt.ref_no || '-',
        "Amount": receipt.amount || '0',
        "Narration": receipt.narration || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Receipts");
      XLSX.writeFile(wb, `Receipts_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export successful!");
    } catch (err) {
      console.error(err);
      message.error("Export failed!");
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const totalAmount = receipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const toggleCard = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Mobile card render function (compact top row)
  const renderMobileCard = (receipt) => {
    const isExpanded = expandedCards[receipt.id];
    return (
      <div key={receipt.id} style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        marginBottom: 12,
        overflow: 'hidden',
        cursor: 'pointer'
      }} onClick={() => toggleCard(receipt.id)}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>{receipt.receipt_no || 'Auto'}</Tag>
            <span style={{ fontSize: 11, fontWeight: 500 }}>{formatDate(receipt.receipt_date)}</span>
          </div>
          <div>
            <Text strong style={{ color: '#52c41a', fontSize: 13 }}>{formatAmount(receipt.amount)}</Text>
            {isExpanded ? <UpOutlined style={{ marginLeft: 6, fontSize: 12 }} /> : <DownOutlined style={{ marginLeft: 6, fontSize: 12 }} />}
          </div>
        </div>
        <div style={{ padding: '6px 12px', display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span><UserOutlined style={{ fontSize: 11 }} /> {receipt.received_from || '-'}</span>
          <span><CreditCardOutlined style={{ fontSize: 11 }} /> {receipt.rec_mode || '-'}</span>
        </div>
        {isExpanded && (
          <div style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0', background: 'white' }}>
            <div style={{ marginBottom: 4, fontSize: 12 }}><strong>Bill No:</strong> {receipt.bill_no || '-'}</div>
            <div style={{ marginBottom: 4, fontSize: 12 }}><strong>Category:</strong> {receipt.rec_category || '-'}</div>
            {receipt.ref_no && <div style={{ marginBottom: 4, fontSize: 12 }}><strong>Ref No:</strong> {receipt.ref_no}</div>}
            {receipt.narration && <div style={{ marginBottom: 4, fontSize: 12 }}><strong>Narration:</strong> {receipt.narration}</div>}
            <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); viewReceipt(receipt.id); }}>View</Button>
              <Button size="small" icon={<FilePdfOutlined />} style={{ color: '#ff4d4f' }} onClick={(e) => { e.stopPropagation(); downloadPDF(receipt); }} loading={pdfLoading}>PDF</Button>
              <Popconfirm title="Delete receipt?" onConfirm={(e) => { e?.stopPropagation(); handleDeleteReceipt(receipt.id, receipt.receipt_no); }}>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>Delete</Button>
              </Popconfirm>
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'SR NO.',
      key: 'srno',
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Receipt No.',
      dataIndex: 'receipt_no',
      key: 'receipt_no',
      render: (text) => <Tag color="blue">{text || 'Auto'}</Tag>
    },
    {
      title: 'Date',
      dataIndex: 'receipt_date',
      key: 'receipt_date',
      render: (text) => <Tag color="geekblue">{formatDate(text)}</Tag>
    },
    {
      title: 'Received From',
      dataIndex: 'received_from',
      key: 'received_from',
      render: (text) => (
        <Space>
          <UserOutlined style={{ color: '#52c41a' }} />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: 'Bill No',
      dataIndex: 'bill_no',
      key: 'bill_no',
      render: (text) => (
        text ? (
          <Tag icon={<FileOutlined />} color="orange">
            {text}
          </Tag>
        ) : <Tag color="default">-</Tag>
      )
    },
    {
      title: 'Payment Mode',
      dataIndex: 'rec_mode',
      key: 'rec_mode',
      render: (text) => (
        <Tag icon={<CreditCardOutlined />} color="purple">
          {text}
        </Tag>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatAmount(text)}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => viewReceipt(record.id)}
            />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button
              type="link"
              size="small"
              icon={<FilePdfOutlined />}
              style={{ color: '#ff4d4f' }}
              onClick={() => downloadPDF(record)}
              loading={pdfLoading}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Receipt"
            description={`Delete ${record.receipt_no || 'receipt'}?`}
            onConfirm={() => handleDeleteReceipt(record.id, record.receipt_no)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Mobile responsive styles - only affects screens <=768px */}
      <style>{`
        @media (max-width: 768px) {
          .ant-statistic-title {
            font-size: 11px !important;
          }
          .ant-statistic-content {
            font-size: 18px !important;
          }
          .ant-card-body {
            padding: 12px !important;
          }
          .ant-statistic-content-prefix {
            font-size: 14px !important;
          }
          .ant-space-item .ant-btn {
            font-size: 12px !important;
            padding: 4px 8px !important;
            height: auto !important;
          }
          .ant-input {
            font-size: 13px !important;
            padding: 6px 8px !important;
          }
          .ant-picker {
            padding: 4px 8px !important;
          }
          .ant-tag {
            font-size: 10px !important;
            padding: 0 6px !important;
            line-height: 18px !important;
          }
          .ant-typography {
            font-size: 12px !important;
          }
        }
      `}</style>

      {/* Stats Cards */}
      {/* Stats Cards - Mobile: 3 in a row (xs=8 each) */}
      <Row gutter={[8, 12]} style={{ marginBottom: 20 }}>
        <Col xs={8} sm={12} lg={8}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: isMobile ? 10 : 20 }}>
            <Statistic
              title="Total Receipts"
              value={receipts.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 14 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={7.5} sm={12} lg={8}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: isMobile ? 10 : 20 }}>
            <Statistic
              title="Total Amount"
              value={totalAmount}
              prefix="₹"
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 14 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={8.5} sm={12} lg={8}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: isMobile ? 10 : 20 }}>
            <Statistic
              title="Average Amount"
              value={receipts.length > 0 ? totalAmount / receipts.length : 0}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#faad14', fontSize: isMobile ? 14 : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter Section */}
      <Card style={{ marginBottom: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: isMobile ? 12 : 20 }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={8}>
            <div>
              <Text strong style={{ fontSize: isMobile ? 11 : 13, display: 'block', marginBottom: 6 }}>Search</Text>
              <Input
                placeholder="Search by receipt no, bill no or party name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
                size={isMobile ? "small" : "middle"}
                prefix={<SearchOutlined />}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div>
              <Text strong style={{ fontSize: isMobile ? 11 : 13, display: 'block', marginBottom: 6 }}>Date Range</Text>
              <RangePicker
                style={{ width: '100%' }}
                size={isMobile ? "small" : "middle"}
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
              />
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space size={isMobile ? 8 : 16} wrap>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading} size={isMobile ? "small" : "middle"}>
                Search
              </Button>
              <Button onClick={handleReset} size={isMobile ? "small" : "middle"}>
                Clear
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportToExcel} loading={exportLoading} size={isMobile ? "small" : "middle"}>
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table or Cards based on screen size */}
      {isMobile ? (
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 2px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : receipts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
              No receipts found.
            </div>
          ) : (
            <>
              {receipts
                .slice(
                  (mobilePage - 1) * mobilePageSize,
                  mobilePage * mobilePageSize
                )
                .map(receipt => renderMobileCard(receipt))}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 15,
                  marginBottom: 15
                }}
              >
                <Pagination
                  current={mobilePage}
                  pageSize={mobilePageSize}
                  total={receipts.length}
                  onChange={(page) => setMobilePage(page)}
                  size="small"
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns}
            dataSource={receipts}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} receipts`,
              showSizeChanger: true,
              showQuickJumper: true
            }}
            size="middle"
            bordered
          />
        </Card>
      )}

      {/* View Modal - unchanged */}
      <Modal
        title={
          <Space size="small" wrap>
            <FileTextOutlined style={{ color: '#1890ff', fontSize: isMobile ? 12 : 14 }} />
            <span style={{ fontSize: isMobile ? 13 : 15 }}>Receipt Details</span>
            <Tag color="blue" style={{ fontSize: isMobile ? 10 : 12, marginLeft: 2 }}>{selectedReceipt?.receipt_no}</Tag>
          </Space>
        }
        open={showModal}
        onCancel={() => setShowModal(false)}
        width={isMobile ? '90%' : 500}
        style={{ top: isMobile ? 60 : 80 }}   // 👈 logo se neeche aane ke liye top barhaya
        bodyStyle={{
          padding: isMobile ? 8 : 16,
          maxHeight: 'calc(100vh - 160px)',
          overflowY: 'auto'
        }}
        footer={[
          <Button
            key="pdf"
            icon={<FilePdfOutlined />}
            style={{ color: '#ff4d4f' }}
            size={isMobile ? "small" : "middle"}
            onClick={() => {
              setShowModal(false);
              downloadPDF(selectedReceipt);
            }}
          >
            {isMobile ? "PDF" : "Download PDF"}
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => setShowModal(false)}
            size={isMobile ? "small" : "middle"}
          >
            Close
          </Button>
        ]}
      >
        {selectedReceipt && (
          <Descriptions
            bordered
            size="small"
            column={isMobile ? 1 : 2}
            labelStyle={{
              fontWeight: 600,
              fontSize: isMobile ? 11 : 12,
              width: isMobile ? '35%' : '30%',
              padding: isMobile ? '4px 6px' : '6px 12px'
            }}
            contentStyle={{
              fontSize: isMobile ? 11 : 12,
              padding: isMobile ? '4px 6px' : '6px 12px'
            }}
          >
            <Descriptions.Item label="Receipt No">{selectedReceipt.receipt_no || '-'}</Descriptions.Item>
            <Descriptions.Item label="Date">{formatDate(selectedReceipt.receipt_date)}</Descriptions.Item>
            <Descriptions.Item label="Received From" span={isMobile ? 1 : 2}>
              {selectedReceipt.received_from}
            </Descriptions.Item>
            <Descriptions.Item label="Bill No" span={isMobile ? 1 : 2}>
              {selectedReceipt.bill_no ? (
                <Tag color="orange" icon={<FileOutlined />} style={{ fontSize: 10 }}>{selectedReceipt.bill_no}</Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Party Name">{selectedReceipt.party_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{selectedReceipt.party_mobile || '-'}</Descriptions.Item>
            <Descriptions.Item label="Payment Mode">{selectedReceipt.rec_mode}</Descriptions.Item>
            <Descriptions.Item label="Category">{selectedReceipt.rec_category || '-'}</Descriptions.Item>
            {selectedReceipt.ref_no && (
              <Descriptions.Item label="Ref No" span={isMobile ? 1 : 2}>
                {selectedReceipt.ref_no}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Amount" span={isMobile ? 1 : 2}>
              <Text strong style={{ color: '#52c41a', fontSize: isMobile ? 14 : 16 }}>
                {formatAmount(selectedReceipt.amount)}
              </Text>
            </Descriptions.Item>
            {selectedReceipt.narration && (
              <Descriptions.Item label="Narration" span={isMobile ? 1 : 2}>
                <span style={{ fontSize: isMobile ? 11 : 12 }}>{selectedReceipt.narration}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ReceiptList;